import { enableES5, setAutoFreeze, setUseProxies } from "immer";
import { AssertWarn, CE, E, emptyArray, RemoveCircularLinks, ToJSON } from "js-vextensions";
import { configure, observable, ObservableMap, ObservableSet, reaction, onReactionError, _getAdministration } from "mobx";
import { BailHandler, RunInAction } from "mobx-graphlink"; // eslint-disable-line
import { observer } from "mobx-react";
import React, { useRef } from "react";
import { EnsureClassProtoRenderFunctionIsWrapped } from "react-vextensions";
import { HandleError } from "../General/Errors.js";
//import {useClassRef} from "react-universal-hooks";
// old: call ConfigureMobX() before any part of mobx tree is created (ie. at start of Store/index.ts); else, immer produce() doesn't work properly
//ConfigureMobX();
ConfigureMobX();
export function ConfigureMobX() {
    // configure({ enforceActions: 'always' });
    configure({ enforceActions: "observed" });
    // have unhandled exceptions in mobx reactions sent to the global error-handler
    onReactionError((error, derivation) => {
        HandleError(error);
    });
    // fixes various issues when Immer is sent mobx objects (see NPMPatches.ts for old fix attempts)
    enableES5(); // es5 mode is needed, since we're not using proxies
    setUseProxies(false);
    setAutoFreeze(false);
}
/** Variant of observer(...) wrapper-func, which returns a simple function result, instead of a ReactJS element-info entry. (*was* needed for ShowMessageBox.message; should be unnecessary now) */
export function observer_simple(target) {
    return observer(target)["type"];
}
// todo: probably rename/replace/rework @Observer decorator to be called @EnhancedComp or something (since it does more than just add mobx observer functionality, it also adds class-hooks and such)
// variant of @observer decorator, which also adds (and is compatible with) class-hooks (similar to mobx-graphlink's @ObserverMGL, but with more options)
export class Observer_Options {
    constructor() {
        this.classHooks = true;
        /*mglObserver = true;
        mglObserver_opts?: ObserverMGL_Options;*/
        // from mobx-graphlink's @ObserverMGL
        this.bailHandler = true;
    }
}
export function Observer(...args) {
    let opts = new Observer_Options();
    if (typeof args[0] == "function") {
        ApplyToClass(args[0]);
    }
    else {
        opts = E(opts, args[0]);
        return ApplyToClass;
    }
    function ApplyToClass(targetClass) {
        if (opts.classHooks)
            ClassHooks(targetClass);
        //if (targetClass instanceof (BaseComponent.prototype as any)) {
        if (targetClass.prototype.PreRender) {
            EnsureClassProtoRenderFunctionIsWrapped(targetClass.prototype);
        }
        /*if (opts.mglObserver) {
            ObserverMGL(opts.mglObserver_opts)(targetClass);
        } else {
            observer(targetClass as any);
        }*/
        if (opts.bailHandler)
            BailHandler(opts.bailHandler_opts)(targetClass);
        observer(targetClass);
    }
}
export function ClassHooks(targetClass) {
    AssertWarn(targetClass.prototype.componentWillMount == null, "ClassHooks encountered a 'componentWillMount' method rather than UNSAFE_componentWillMount; ignoring/dropping its functionality.");
    const attachProp = targetClass.prototype.ComponentWillMount ? "ComponentWillMount" : "UNSAFE_componentWillMount";
    const componentWillMount_orig = targetClass.prototype[attachProp];
    targetClass.prototype[attachProp] = function () {
        const MAGIC_STACKS = GetMagicStackSymbol(this);
        if (!this[MAGIC_STACKS]) {
            // by initializing comp[MAGIC_STACKS] ahead of time, we keep react-universal-hooks from patching this.render
            this[MAGIC_STACKS] = {};
        }
        if (componentWillMount_orig)
            return componentWillMount_orig.apply(this, arguments);
    };
    const render_orig = targetClass.prototype.render;
    // note our patching Class.render, not instance.render -- this is compatible with mobx-react
    targetClass.prototype.render = function () {
        const MAGIC_STACKS = GetMagicStackSymbol(this);
        if (this[MAGIC_STACKS]) {
            // apply the stack-resetting functionality normally done in the on-instance patched this.render
            Object.getOwnPropertySymbols(this[MAGIC_STACKS]).forEach(k => {
                this[MAGIC_STACKS][k] = 0;
            });
        }
        return render_orig.apply(this, arguments);
    };
}
let magicStackSymbol_cached;
export function GetMagicStackSymbol(comp) {
    if (magicStackSymbol_cached == null) {
        const instanceKey = React.version.indexOf("15") === 0 ? "_instance" : "stateNode";
        const ReactInternals = React["__SECRET_INTERNALS_DO_NOT_USE_OR_YOU_WILL_BE_FIRED"];
        const compBeingRendered_real = ReactInternals.ReactCurrentOwner.current;
        const compBeingRendered_fake = { render: () => ({}) };
        ReactInternals.ReactCurrentOwner.current = { [instanceKey]: compBeingRendered_fake };
        // eslint-disable-next-line
        {
            //useClassRef(); // more straight-forward, but involves `require("react-universal-hooks")` from web-vcore, which is nice to be able to avoid
            /*const useRefIsModified = useRef["isModified"] ?? (useRef["isModified"] = useRef.toString().includes("useClassRef"));
            if (!useRefIsModified) throw new Error("Cannot get magic-stack symbol, because react-universal-hooks has not overridden the React.useRef function.");*/
            useRef(); // this triggers react-universal-hooks to attach data to the "comp being rendered" (fake object above)
        }
        ReactInternals.ReactCurrentOwner.current = compBeingRendered_real;
        // now we can obtain the secret magic-stacks symbol, by iterating the symbols on compBeingRendered_fake
        const symbols = Object.getOwnPropertySymbols(compBeingRendered_fake);
        const magicStackSymbol = symbols.find(a => a.toString() == "Symbol(magicStacks)");
        magicStackSymbol_cached = magicStackSymbol;
    }
    return magicStackSymbol_cached; // needed for ts to allow as index
}
export function StoreAction(...args) {
    let name, actionFunc;
    if (typeof args[0] == "function")
        [actionFunc] = args;
    else
        [name, actionFunc] = args;
    /* if (name) action["displayName"] = name;
    action["Watch"] = function(...callArgs) {
        //const accessor_withCallArgsBound = accessor.bind(null, ...callArgs); // bind is bad, because it doesn't "gobble" the "watcher" arg
        const action_withCallArgsBound = ()=>{
            //return accessor.apply(this, callArgs);
            return action(...callArgs);
            //return accessor_withProfiling(...callArgs);
        };
        if (name) action_withCallArgsBound["displayName"] = name;
        //outerAccessor["callArgs"] = callArgs;
        //outerAccessor["displayName"] = `${name || "Unknown"}(${callArgs.join(", ")})`;
        return Watch(action_withCallArgsBound, dependencies);
    };
    return action as any; */
    //let action_final = action(name, actionFunc);
    const result = (...callArgs) => {
        let name_withArgs = name;
        name_withArgs += `(${callArgs.map(a => (a != null ? ToJSON(a) : "null")).join(", ")})`;
        return RunInAction(name_withArgs, () => actionFunc(...callArgs));
    };
    // result["isStoreAction"] = true; // mark export as store-action (for copying into mobx-state-tree actions collection)
    return result;
}
const observableWarningGivenFor = new WeakSet();
export const O = ((target, propertyKey) => {
    //if (target.constructor instanceof Function && !target.constructor.toString().includes("makeObservable(")) {
    if (target.constructor instanceof Function && !target.constructor.toString().includes("makeObservable")) { // transpilation makes only the raw name safe to look for
        if (!observableWarningGivenFor.has(target.constructor)) {
            console.warn(`The @O decorator was used on "${target.constructor.name}.${String(propertyKey)}", but the class is missing the "makeObservable(this);" call. See here for more info: https://mobx.js.org/enabling-decorators.html`);
            observableWarningGivenFor.add(target.constructor);
        }
    }
    return observable(target, propertyKey);
});
// copy ".ref", etc. fields from "observable" (not wrapped)
for (const [key, descriptor] of Object.entries(Object.getOwnPropertyDescriptors(observable))) {
    Object.defineProperty(O, key, descriptor);
}
//export {RunInAction} from "mobx-graphlink";
export { RunInAction };
export function RunInAction_Set(...args) {
    var _a;
    let classInstance, setterFunc;
    if (args.length == 1)
        [setterFunc] = args;
    else
        [classInstance, setterFunc] = args;
    const funcStr = setterFunc.toString();
    const funcStr_namePartMatch = funcStr.match(/(store.+?) /);
    const actionName = `Set${classInstance ? `@${classInstance.constructor.name}` : ""}:${(_a = funcStr_namePartMatch === null || funcStr_namePartMatch === void 0 ? void 0 : funcStr_namePartMatch[1]) !== null && _a !== void 0 ? _a : funcStr}`;
    RunInAction(actionName, setterFunc);
}
export var GetMobXStoredAnnotationSymbol_cached;
export function GetMobXStoredAnnotationSymbol(objectsPossiblyHavingSymbol) {
    if (GetMobXStoredAnnotationSymbol_cached == null) {
        for (const obj of objectsPossiblyHavingSymbol) {
            if (obj == null)
                continue;
            //const newSymbol = ObjectCE(obj).FindSym("mobx-stored-annotations");
            const symbols = Object.getOwnPropertySymbols(obj);
            const matchingSymbol = symbols.find(a => a.toString() == `Symbol(mobx-stored-annotations)`);
            if (matchingSymbol) {
                GetMobXStoredAnnotationSymbol_cached = matchingSymbol;
                break;
            }
        }
    }
    return GetMobXStoredAnnotationSymbol_cached;
}
export function GetMobXStoredAnnotations(mobxTree) {
    const mobxTree_proto = mobxTree != null ? Object.getPrototypeOf(mobxTree) : null;
    //const symbol = storedAnnotationsSymbol;
    const symbol = GetMobXStoredAnnotationSymbol([mobxTree_proto, mobxTree]);
    // usually the actual annotations are on the class-prototype, so search there first
    if ((mobxTree_proto === null || mobxTree_proto === void 0 ? void 0 : mobxTree_proto[symbol]) != null)
        return mobxTree_proto === null || mobxTree_proto === void 0 ? void 0 : mobxTree_proto[symbol];
    // else, fall back to passed object itself
    return mobxTree === null || mobxTree === void 0 ? void 0 : mobxTree[symbol];
}
// mobx-mirror
// ==========
export class GetMirrorOfMobXTree_Options {
    constructor() {
        /** Most callers of GetMirrorOfMobXTree only care to have mobx-prop pathways mirrored, and excluding the rest improves perf substantially. */
        //onlyCopyMobXNodes = true;
        this.onlyCopyMobXProps = true;
        this.mirrorObservableRefs = false; // why is this false by default?
        /** If enabled, removes circular-links from mirror tree. This doesn't affect original object-tree, and makes the mirror tree usable in immer.produce(). */
        this.removeCircularLinks = false; // disabled by default, since onlyCopyMobXNodes is usually sufficient (and enabling this just adds some slowdown)
        /** List of classes for which instances in source-tree will have their copy-instances assigned the same prototype. */
        this.prototypesToKeep = [Array, Map, Set];
    }
}
/**
Creates a deep copy of the object-tree passed in; for source nodes that are mobx objects, creates dynamically-updating "mirrors".
Purpose: Enables use of MobX object-trees as the source/base object for immer.produce(). (see: https://github.com/immerjs/immer/issues/515)
Important note: Due to technical reasons, the mobx-tree's "mirror" will be empty for the first call, *if* call-stack already in mobx mutate-batch (ie. globalState.inBatch > 0). Mirror will be populated just after call-stack completes.
*/
export function GetMirrorOfMobXTree(mobxTree, opt = new GetMirrorOfMobXTree_Options()) {
    if (mobxTree == null)
        return null;
    try {
        mobxTree["$mirror"];
    }
    catch (ex) {
        // if mere prop-access fails, we must have hit a different-domain frame context object, which prevents prop-access; just return empty object
        return {};
    }
    if (mobxTree["$mirror"] == null) {
        const tree_plainMirror = Array.isArray(mobxTree) ? [] :
            mobxTree instanceof Map || mobxTree instanceof ObservableMap ? new Map() :
                mobxTree instanceof Set || mobxTree instanceof ObservableSet ? new Set() :
                    {};
        if (CE(opt.prototypesToKeep).Any(a => mobxTree instanceof a)) {
            Object.setPrototypeOf(tree_plainMirror, Object.getPrototypeOf(mobxTree));
        }
        if (Object.isExtensible(mobxTree)) {
            Object.defineProperty(mobxTree, "$mirror", { value: tree_plainMirror });
        }
        StartUpdatingMirrorOfMobXTree(mobxTree, tree_plainMirror, opt);
    }
    if (opt.removeCircularLinks) {
        RemoveCircularLinks(mobxTree["$mirror"]);
    }
    return mobxTree["$mirror"];
}
/** Wrapper around _getAdministration that returns null when encountering a non-mobx object, rather than erroring. */
export function GetAdministration_Safe(possibleMobXTree) {
    try {
        //return mobxTree[$mobx];
        return _getAdministration(possibleMobXTree);
    }
    catch (ex) {
        return null;
    }
}
export function StartUpdatingMirrorOfMobXTree(mobxTree, tree_plainMirror, opt = new GetMirrorOfMobXTree_Options()) {
    //const stopUpdating = autorun(()=>{
    reaction(() => {
        var _a, _b, _c;
        const sourceIsMap = mobxTree instanceof Map || mobxTree instanceof ObservableMap;
        const targetIsMap = tree_plainMirror instanceof Map || tree_plainMirror instanceof ObservableMap;
        const mobxInfo = GetAdministration_Safe(mobxTree);
        const keys = sourceIsMap ? mobxTree.keys() : Object.keys(mobxTree); // always access the keys, to ensure the autorun subscribes to them (using the mobx-admin-object path doesn't do this)
        //const keys = sourceIsMap || mobxInfo ? mobx_keys(mobxTree) : Object.keys(mobxTree); // always access the keys, to ensure the autorun subscribes to them (using the mobx-admin-object path doesn't do this)
        const mobxKeys = 
        // if mobxTree is an Observable[Map/Set], then all of its keys are "mobx props"/reactive
        opt.onlyCopyMobXProps && !(mobxTree instanceof ObservableMap || mobxTree instanceof ObservableSet)
            //? mobxInfo?.keys_() ?? emptyArray
            // Why do we use x.values_.keys() rather than x.keys_()? Because the former works on both Map's and arrays.
            //? [...(mobxInfo?.values_?.keys() ?? emptyArray)] // converting to array makes debugging a bit nicer (fixes watch-panel glitch of showing no-items on 2nd+ views), but it's not necessary
            ? (_b = (_a = mobxInfo === null || mobxInfo === void 0 ? void 0 : mobxInfo.values_) === null || _a === void 0 ? void 0 : _a.keys()) !== null && _b !== void 0 ? _b : emptyArray
            : keys;
        const mobxStoredAnnotations = GetMobXStoredAnnotations(mobxTree);
        for (const key of mobxKeys) {
            const valueFromSource = sourceIsMap ? mobxTree.get(key) : mobxTree[key]; // this counts as a mobx-get, meaning the autorun subscribes, so this func reruns when the prop-value changes
            //const valueFromSource = sourceIsMap || mobxInfo ? mobx_get(mobxTree, key) : mobxTree[key]; // this counts as a mobx-get, meaning the autorun subscribes, so this func reruns when the prop-value changes
            const fieldObservedAsRefOnly = ((_c = mobxStoredAnnotations === null || mobxStoredAnnotations === void 0 ? void 0 : mobxStoredAnnotations[key]) === null || _c === void 0 ? void 0 : _c.annotationType_) == "observable.ref" || GetAdministration_Safe(valueFromSource) == null;
            let valueForTarget;
            if (typeof valueFromSource == "object" && valueFromSource != null && (opt.mirrorObservableRefs || !fieldObservedAsRefOnly)) {
                //if (!opt.onlyCopyMobXNodes || valueFromSource[$mobx] != null) {
                valueForTarget = GetMirrorOfMobXTree(valueFromSource, opt.removeCircularLinks ? E(opt, { removeCircularLinks: false }) : opt);
            }
            else {
                valueForTarget = valueFromSource;
            }
            if (targetIsMap) {
                tree_plainMirror.set(key, valueForTarget);
                //mobx_set(tree_plainMirror, key, valueForTarget);
            }
            else {
                tree_plainMirror[key] = valueForTarget;
            }
            //if (typeof valueForTarget_old == "object" && valueForTarget_old["$mirror_stopUpdating"]) { [...]
        }
    }, () => {
        var _a;
        (_a = opt.onChange) === null || _a === void 0 ? void 0 : _a.call(opt, mobxTree, tree_plainMirror);
    }, {
        // this makes-so when reaction/first-func runs, the effect/second-func runs as well (we split them simply to prevent onChange from adding to the observed data)
        equals: () => false,
    });
    // attempted fix for first autorun call not immediately populating the mobx-mirror, if already in mobx mutate-batch (ie. when globalState.inBatch > 0) [canceled; too involved]
    /*{
        // args copied from: https://github.com/mobxjs/mobx/blob/0d28db8a0ba99f5cce744bb83b5bd88ec45a7e41/packages/mobx/src/api/autorun.ts#L50
        scheduler: new Reaction_AlwaysDoImmediately(
            name: "Autorun [using Reaction_AlwaysDoImmediately]", //name,
            function (this: Reaction) {
                 this.track(reactionRunner)
            },
            null, //opts.onError,
            opts.requiresObservable
      ),
    });*/
    //Object.defineProperty(mobxTree, "$mirror_stopUpdating", stopUpdating);
}
/*export class Reaction_AlwaysDoImmediately extends Reaction {
    schedule_() {
        if (!this["isScheduled_"]) {
            this["isScheduled_"] = true
            globalState.pendingReactions.push(this)
            runReactions()
        }
    }
}*/ 
//# sourceMappingURL=MobX.js.map